import { useMemo, useRef, useState } from 'react';

import type { FilterableOption } from './filterOptionsByVisibleText';
import { filterOptionsByVisibleText } from './filterOptionsByVisibleText';

interface NarrowingCache<T> {
  options: readonly T[] | undefined;
  needle: string;
  result: readonly T[] | undefined;
}

/**
 * Owns the pre-filter state shared by the Select and MultiSelect wrappers — the
 * filter box's text, and the narrowed option array derived from it. Both
 * wrappers held an identical copy of this before; keep it here so the two can't
 * drift.
 *
 * `isEnabled` mirrors the wrappers' `preFilter` flag: when the caller asked for
 * a filteringType we don't override, the options pass through untouched.
 *
 * Cost. The filter runs on every keystroke over the whole option array, which
 * for a large tenant's owner picker is ~12k entries. Two things keep that off
 * the critical path:
 *
 *   1. `filterOptionsByVisibleText` memoises each option's lowercased
 *      searchable text (see there), so a keystroke is one `includes` per
 *      option rather than five `toLowerCase` allocations.
 *   2. Adding characters narrows the PREVIOUS result instead of re-scanning the
 *      full array. Substring matching is monotone — an option that fails
 *      `includes('ab')` cannot pass `includes('abc')` — so typing forwards gets
 *      progressively cheaper, and the expensive full pass happens only on the
 *      first character, on backspace, or when the caller swaps the array.
 *
 * Group monotonicity holds too: a group whose own label matched the shorter
 * needle is retained WHOLE (all children), so re-filtering it against a longer
 * needle still sees the complete child set. A group retained only for matching
 * children carries exactly the children that matched the shorter needle, which
 * is a superset of those matching the longer one.
 *
 * Note this optimises our own pass only. It is NOT what makes a large picker
 * usable — that is Cloudscape's `virtualScroll`, which bounds the number of
 * mounted option components. See the comment in Select.tsx before touching
 * either.
 */
export const useVisibleOptions = <T extends FilterableOption>(
  options: readonly T[] | undefined,
  isEnabled: boolean
) => {
  const [filteringText, setFilteringText] = useState('');
  const previous = useRef<NarrowingCache<T> | null>(null);

  const visibleOptions = useMemo(() => {
    if (!isEnabled) {
      return options;
    }

    const last = previous.current;
    const canNarrowPrevious =
      !!last &&
      last.options === options &&
      last.needle !== '' &&
      filteringText.startsWith(last.needle);

    const result = filterOptionsByVisibleText(
      canNarrowPrevious ? last.result : options,
      filteringText
    );
    // Written during render, which is safe here only because the value is a
    // pure function of the deps: a StrictMode double-invoke re-narrows an
    // already-narrowed set with the same needle, which is idempotent.
    previous.current = { options, needle: filteringText, result };

    return result;
  }, [options, filteringText, isEnabled]);

  return { visibleOptions, setFilteringText };
};
